import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Book, Search, Info, ChevronRight } from 'lucide-react';
import { educationModules } from '../constants/educationData';

const constructionTerms = [
  {
    term: 'K3 (Keamanan, Kesehatan, dan Keselamatan Kerja)',
    definition: 'Segala kegiatan untuk menjamin dan melindungi keselamatan dan kesehatan tenaga kerja melalui upaya pencegahan kecelakaan kerja dan penyakit akibat kerja.',
    category: 'Manajemen'
  },
  {
    term: 'RAB (Rencana Anggaran Biaya)',
    definition: 'Perhitungan banyaknya biaya yang diperlukan untuk bahan dan upah, serta biaya-biaya lain yang berhubungan dengan pelaksanaan bangunan atau proyek.',
    category: 'Manajemen'
  },
  {
    term: 'HPS (Harga Perkiraan Sendiri)',
    definition: 'Kalkulasi biaya atas barang/jasa yang ditetapkan oleh PPK yang digunakan sebagai alat untuk menilai kewajaran harga penawaran.',
    category: 'Pengadaan'
  },
  {
    term: 'BOQ (Bill of Quantities)',
    definition: 'Dokumen yang merinci volume pekerjaan, bahan, dan tenaga kerja yang diperlukan dalam sebuah proyek konstruksi.',
    category: 'Administrasi'
  },
  {
    term: 'Kontrak Payung (Umbrella Contract)',
    definition: 'Kontrak harga satuan antara pemerintah dengan penyedia untuk barang/jasa yang volumenya belum dapat ditentukan secara pasti.',
    category: 'Pengadaan'
  },
  {
    term: 'Addendum Kontrak',
    definition: 'Ketentuan tambahan atau perubahan tertulis pada kontrak yang telah disepakati sebelumnya oleh para pihak.',
    category: 'Kontrak'
  },
  {
    term: 'Mutual Check Nol (MC-0)',
    definition: 'Kegiatan perhitungan kembali volume item pekerjaan dan pengamatan kondisi lapangan untuk membandingkan antara gambar rencana dengan kondisi aktual di lokasi.',
    category: 'Pelaporan'
  },
  {
    term: 'Kurva-S',
    definition: 'Grafik yang menggambarkan hubungan antara persentase kumulatif biaya atau bobot pekerjaan dengan waktu pelaksanaan proyek.',
    category: 'Pelaporan'
  },
  {
    term: 'Provisional Hand Over (PHO)',
    definition: 'Serah terima pertama seluruh hasil pekerjaan konstruksi dari penyedia jasa kepada pengguna jasa setelah pekerjaan selesai 100%.',
    category: 'Kontrak'
  },
  {
    term: 'Final Hand Over (FHO)',
    definition: 'Penyerahan akhir pekerjaan setelah penyedia menyelesaikan masa pemeliharaan dan memperbaiki semua kerusakan yang ada.',
    category: 'Kontrak'
  },
  {
    term: 'Jaminan Pelaksanaan',
    definition: 'Jaminan tertulis yang bersifat tanpa syarat dan mudah dicairkan yang diserahkan penyedia untuk menjamin penyelesaian pekerjaan.',
    category: 'Pengadaan'
  },
  {
    term: 'Jaminan Uang Muka',
    definition: 'Jaminan yang diberikan penyedia kepada pengguna jasa atas penerimaan uang muka untuk mulai melaksanakan pekerjaan.',
    category: 'Pengadaan'
  },
  {
    term: 'SND (Surat Negosiasi Dan Klarifikasi)',
    definition: 'Dokumen yang berisi risalah hasil kesepakatan teknis dan harga antara pokja pemilihan dengan calon pemenang lelang.',
    category: 'Pengadaan'
  },
  {
    term: 'BAHP (Berita Acara Hasil Pelelangan)',
    definition: 'Dokumen resmi yang merangkum hasil evaluasi administrasi, teknis, dan harga dari seluruh peserta tender.',
    category: 'Pengadaan'
  },
  {
    term: 'Shop Drawing',
    definition: 'Gambar teknik yang dibuat oleh kontraktor sebagai panduan detail pelaksanaan pekerjaan di lapangan.',
    category: 'Teknis'
  },
  {
    term: 'As-Built Drawing',
    definition: 'Gambar rekaman akhir yang dibuat sesuai dengan kondisi terpasang di lapangan setelah pekerjaan selesai.',
    category: 'Teknis'
  },
  {
    term: 'Beton Bertulang',
    definition: 'Beton yang dikombinasikan dengan baja tulangan untuk meningkatkan kekuatan tarik material.',
    category: 'Struktur'
  },
  {
    term: 'Batching Plant',
    definition: 'Tempat pengolahan atau pencampuran bahan baku beton ready mix dalam skala besar.',
    category: 'Alat Berat'
  },
  {
    term: 'Back Up Data',
    definition: 'Dokumen pendukung kuantitas dan kualitas yang melampiri setiap pengajuan sertifikat pembayaran atau termin.',
    category: 'Pelaporan'
  },
  {
    term: 'Keadaan Kahar (Force Majeure)',
    definition: 'Suatu kejadian yang terjadi di luar kemampuan, kesalahan, atau kekuasaan para pihak dan tidak dapat diduga sebelumnya.',
    category: 'Kontrak'
  },
  {
    term: 'Denda Keterlambatan (Liquidated Damages)',
    definition: 'Sanksi finansial yang dikenakan kepada penyedia jika gagal menyelesaikan pekerjaan dalam jangka waktu kontrak.',
    category: 'Kontrak'
  },
  {
    term: 'Change Contract Order (CCO)',
    definition: 'Perubahan kontrak yang meliputi tambah/kurang volume, perubahan spesifikasi teknis, atau penambahan item pekerjaan baru.',
    category: 'Kontrak'
  },
  {
    term: 'Penyelesaian Perselisihan',
    definition: 'Ketentuan mengenai tata cara penyelesaian sengketa kontrak, baik melalui musyawarah, mediasi, arbitrase, atau pengadilan.',
    category: 'Kontrak'
  },
  {
    term: 'Uang Retensi',
    definition: 'Uang yang ditahan sementara (biasanya 5%) sebagai jaminan bagi pengguna jasa atas pemeliharaan pekerjaan oleh penyedia.',
    category: 'Keuangan'
  },
  {
    term: 'Perpanjangan Waktu (Extension of Time)',
    definition: 'Penambahan waktu pelaksanaan proyek yang disepakati melalui addendum akibat adanya kompensasi atau keadaan kahar.',
    category: 'Kontrak'
  },
  {
    term: 'Catatan Lapangan (Field Note)',
    definition: 'Buku atau lembar instruksi/catatan harian dari pengawas kepada penyedia mengenai pelaksanaan teknis di lapangan.',
    category: 'Pelaporan'
  },
  {
    term: 'Direksi Keet',
    definition: 'Bangunan sementara yang digunakan sebagai kantor koordinasi, pengawasan, dan administrasi di lokasi proyek.',
    category: 'Fasilitas'
  },
  {
    term: 'Rencana Mutu Kontrak (RMK)',
    definition: 'Dokumen sistem manajemen mutu yang disusun oleh penyedia untuk menjamin kualitas pekerjaan selama masa kontrak.',
    category: 'Manajemen'
  }
];

export default function ConstructionEducation() {
  const [activeTab, setActiveTab] = useState<'modules' | 'glossary'>('modules');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTerms = constructionTerms.filter(item =>
    item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-navy pt-20 pb-12 px-6 rounded-b-[3rem] shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full -mr-20 -mt-20 blur-3xl"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 text-gold text-xs font-black uppercase tracking-widest mb-6"
          >
            <Book className="h-4 w-4" />
            Pusat Edukasi Konstruksi
          </motion.div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Tingkatkan <span className="text-gold">Wawasan</span> Anda
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Pelajari standar teknis, regulasi terbaru, dan istilah profesional dalam industri jasa konstruksi Indonesia.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-20">
        {/* Tabs Control */}
        <div className="flex bg-card p-1.5 rounded-2xl shadow-xl border border-border mb-10 w-fit mx-auto">
          <button
            onClick={() => setActiveTab('modules')}
            className={`px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all ${
              activeTab === 'modules' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            Modul Belajar
          </button>
          <button
            onClick={() => setActiveTab('glossary')}
            className={`px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all ${
              activeTab === 'glossary' ? 'bg-primary text-primary-foreground shadow-lg' : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            Glosarium
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'modules' ? (
            <motion.div
              key="modules"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {educationModules.map((module, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-card hover:bg-accent border border-border rounded-3xl p-6 transition-all shadow-sm hover:shadow-xl"
                >
                  <Link to={`/education/${module.slug}`} className="flex items-start gap-4">
                    <div className={`h-14 w-14 rounded-2xl shrink-0 flex items-center justify-center ${module.color} transition-transform group-hover:scale-110`}>
                      <module.icon className="h-7 w-7" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">{module.category}</span>
                      </div>
                      <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">{module.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed">
                        {module.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-primary group-hover:gap-3 transition-all">
                        Pelajari Sekarang <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="glossary"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {/* Search Bar */}
              <div className="relative mb-8 max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Cari istilah konstruksi..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 rounded-2xl bg-card border border-border focus:ring-2 focus:ring-primary shadow-lg outline-none text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTerms.map((item, index) => (
                  <div key={index} className="bg-card border border-border rounded-2xl p-5 hover:border-gold/30 transition-all">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold"></div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-gold">{item.category}</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">{item.term}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed italic">
                      "{item.definition}"
                    </p>
                  </div>
                ))}
              </div>
              
              {filteredTerms.length === 0 && (
                <div className="text-center py-20 bg-card/50 rounded-3xl border border-dashed border-border">
                  <Info className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium">Maaf, istilah tidak ditemukan.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
